import { useState } from 'react';
import {
    useChannelActionContext,
    useMessageInputContext,
    useChannelStateContext,
} from 'stream-chat-react';
import { makeStyles } from '@material-ui/styles';

import { useChatClient } from '@bizzabo/chat-core';
import { InputFooter } from '@bizzabo/collaboration-common';

import useTypeTracking from '../../../../../analytics';
import { createMessagePreview } from './createMessagePreview';
import { sendMessageRequest } from '../../../utils/sendMessageUtils';

const preventClickAwayElements = ['emoji-picker'];
const PLACEHOLDER_TEXT = 'Start a new conversation';
const maxMessageLength = 400;

const CustomMessageInput: React.FC = () => {
    const classes = useStyles();
    const { trackSystemEvent } = useTypeTracking();
    const { handleChange } = useMessageInputContext();
    const [inputOpen, setInputOpen] = useState(true);
    const [message, setMessage] = useState({ text: '', anonymous: false });
    const [isReactionsOpen, setIsReactionsOpen] = useState(false);
    const { updateMessage } = useChannelActionContext();
    const { channel } = useChannelStateContext();
    const { client } = useChatClient();

    const sendSnowplowEvent = (actionName: string, messageId: string) => {
        const payload = {
            action: actionName,
            message_type: 'text',
            channel_type: channel.type,
            messageId: messageId,
        };

        trackSystemEvent({
            description: payload,
        });
    };

    const handleSendMessage = async () => {
        const { text } = message;
        const messagePreview = createMessagePreview(client, text);
        updateMessage(messagePreview);
        sendSnowplowEvent('MESSAGE_SENT_ATTEMPT', messagePreview.id);
        const messageSentSuccessfully = await sendMessageRequest(
            channel,
            messagePreview,
            updateMessage
        );
        if (messageSentSuccessfully) {
            sendSnowplowEvent('MESSAGE_SENT_SUCCESS', messagePreview.id);
        }

        setMessage({ text: '', anonymous: false });
        setIsReactionsOpen(false);
        setInputOpen(false);
    };

    const toggleExpand = (expand: boolean) => {
        setInputOpen(expand);
    };

    const toggleEmojiPicker = (expand: boolean) => {
        setIsReactionsOpen(expand);
    };

    return (
        <div className={classes.inputWrapper}>
            <InputFooter
                toggleInputExpand={toggleExpand}
                placeholderText={PLACEHOLDER_TEXT}
                message={message}
                setMessage={setMessage}
                isInputOpen={inputOpen}
                enableAnonymousMessage={false}
                preventClickAwayElements={preventClickAwayElements}
                sendMessage={handleSendMessage}
                isSendDisabled={!inputOpen || !message.text.trim().length}
                displayReactionsButton={true}
                isReactionsOpen={isReactionsOpen}
                toggleReactions={toggleEmojiPicker}
                isEmojiButtonDisabled={!inputOpen}
                maxInputLength={maxMessageLength}
                onChange={handleChange}
                fontSizeMobile={16}
            />
        </div>
    );
};

const useStyles = makeStyles(
    theme => ({
        inputWrapper: {
            backgroundColor: theme.palette.greys[100],
        },
    }),
    { classNamePrefix: 'custom-message-input' }
);

export default CustomMessageInput;
